
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export type ColumnId = "name" | "category" | "type" | "rating" | "complexity" | "filters" | "actions";

export interface Column {
  id: ColumnId;
  label: string;
  className?: string;
  draggable: boolean;
  sortable: boolean;
}

// Define a type for the column data that will be stored in the database
// This ensures compatibility with Supabase's Json type
interface StorableColumn {
  id: string;
  label: string;
  className?: string;
  draggable: boolean;
  sortable: boolean;
}

export const useTableColumns = () => {
  const { user } = useAuth();
  // Define initial columns array in the requested order (rating, name, requirements)
  const defaultColumns: Column[] = [
    {
      id: "rating",
      label: "Rating",
      className: "w-[140px]",
      draggable: true,
      sortable: true,
    },
    {
      id: "name",
      label: "Name",
      className: "min-w-[250px]",
      draggable: true,
      sortable: true,
    },
    {
      id: "complexity",
      label: "Requirements",
      className: "hidden md:table-cell",
      draggable: true,
      sortable: true,
    },
    {
      id: "category",
      label: "Category",
      className: "hidden md:table-cell",
      draggable: true,
      sortable: true,
    },
    {
      id: "type",
      label: "Type",
      className: "hidden md:table-cell",
      draggable: true,
      sortable: true,
    },
    {
      id: "filters",
      label: "Filters",
      className: "hidden md:table-cell",
      draggable: true,
      sortable: false,
    },
    {
      id: "actions",
      label: "Save",
      className: "hidden md:table-cell w-[100px]",
      draggable: false,
      sortable: false,
    },
  ];

  // State for columns
  const [columns, setColumns] = useState<Column[]>(defaultColumns);
  const [isSaving, setIsSaving] = useState(false);

  // Load user's column preferences when component mounts
  useEffect(() => {
    const loadColumnPreferences = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('user_preferences')
          .select('column_order')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error loading column preferences:', error);
          return;
        }

        if (data && data.column_order) {
          // If user has saved column preferences, use them
          const savedColumnData = data.column_order;
          
          // Type guard to check if savedColumns is an array
          if (Array.isArray(savedColumnData)) {
            // We need to validate that the saved data has the correct structure
            // and safely convert it to the Column type
            
            // First, check if each item in the array is a valid object with the expected structure
            const validColumns = savedColumnData
              .filter((col): col is Record<string, any> => {
                // Make sure each item has all required properties
                return (
                  typeof col === 'object' && 
                  col !== null && 
                  'id' in col && 
                  'label' in col &&
                  'draggable' in col &&
                  'sortable' in col
                );
              })
              .filter(col => {
                // Make sure the id is a valid ColumnId
                const validIds: string[] = ["name", "category", "type", "rating", "complexity", "filters", "actions"];
                return typeof col.id === 'string' && validIds.includes(col.id);
              })
              .map(col => {
                // Convert to Column type
                return {
                  id: col.id as ColumnId,
                  label: col.label as string,
                  className: col.className as string | undefined,
                  draggable: Boolean(col.draggable),
                  sortable: Boolean(col.sortable)
                };
              });
            
            // Get column IDs from the validated array
            const savedColumnIds = validColumns.map(col => col.id);
            
            // Add any missing columns at the end
            const missingColumns = defaultColumns.filter(
              col => !savedColumnIds.includes(col.id)
            );
            
            if (missingColumns.length > 0) {
              setColumns([...validColumns, ...missingColumns]);
            } else {
              setColumns(validColumns);
            }
          }
        }
      } catch (error) {
        console.error('Error loading column preferences:', error);
      }
    };
    
    loadColumnPreferences();
  }, [user]);

  // Save column order to supabase
  const saveColumnOrder = async () => {
    if (!user) {
      toast.error("Please log in to save preferences");
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Create a plain object array that can be safely stored as JSON
      // Convert Column[] to StorableColumn[] which is compatible with the Json type
      const columnOrderForStorage = columns.map(col => ({
        id: col.id,
        label: col.label,
        className: col.className || "",
        draggable: col.draggable,
        sortable: col.sortable
      }));

      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          id: user.id,
          column_order: columnOrderForStorage
        });
        
      if (error) {
        throw error;
      }
      
      toast.success("Column order saved successfully");
    } catch (error) {
      console.error('Error saving column order:', error);
      toast.error("Failed to save column order");
    } finally {
      setIsSaving(false);
    }
  };

  return {
    columns,
    setColumns,
    saveColumnOrder,
    isSaving
  };
};
